import { ApolloError, ServerError } from "@apollo/client";
import * as Sentry from "@sentry/react";
import { GraphQLError } from "graphql";
import { ReactNode, useCallback } from "react";

import { redactThing } from "@rewards-web/shared/lib/redact-thing";

import { ErrorPage } from "../../pages/error";

export type TransformBreadcrumbFn = (
  breadcrumb: Sentry.Breadcrumb,
  hint?: Sentry.BreadcrumbHint
) => Sentry.Breadcrumb;

export type AllowUrls = Sentry.BrowserOptions["allowUrls"];

export function useSetErrorTrackingIdentity() {
  return useCallback((userId: string) => Sentry.setUser({ id: userId }), []);
}

export function useClearErrorTrackingIdentity() {
  return () => Sentry.setUser(null);
}

/**
 * Reports an error to the error reporting service
 */
export function reportError(error: any) {
  captureError(error);
}

function captureError(error: any) {
  // eslint-disable-next-line no-console
  console.error(
    redactThing(error, { redactEmails: true, redactPhoneNumbers: true })
  );

  if (error instanceof ApolloError) {
    // for apollo errors, we want to capture the operation name and query string,
    // so we know which specific graphql operation failed
    // (the stack trace is unfortunately unhelpful)
    const defaultCaptureContext = {
      tags: {
        graphql_operation_name: error.extraInfo?.operationName,
      },
      contexts: {
        ...(error.extraInfo
          ? {
              graphql_operation: {
                operationName: error.extraInfo?.operationName,
                query: error.extraInfo?.query,
              },
            }
          : {}),
      },
      fingerprint: [
        "{{ default }}",

        // fingerprint by operation name to distinguish between
        // different graphql operations
        // (since stack trace is unlikely to be helpful)
        ...(error.extraInfo?.operationName
          ? [error.extraInfo.operationName]
          : []),
      ],
    };

    if (error.graphQLErrors && error.graphQLErrors.length > 0) {
      // if we have graphql errors, we want to capture those individually

      for (const graphQLError of error.graphQLErrors) {
        // apollo client types these as plain (formatted) errors, and only
        // sometimes instantiates `GraphQLError`
        const graphQLErrorInstance =
          graphQLError instanceof GraphQLError ? graphQLError : undefined;
        let sentryError: Error;

        if (graphQLErrorInstance) {
          sentryError = graphQLErrorInstance;
        } else {
          // ensure it reads "GraphQLError" in the error name.
          sentryError = new Error(graphQLError.message);
          sentryError.name = "GraphQLError";
        }

        scrubAndCaptureException(sentryError, {
          ...defaultCaptureContext,
          contexts: {
            ...defaultCaptureContext.contexts,
            graphql_error_details: {
              message: graphQLError.message,
              name: graphQLErrorInstance?.name,
              locations: graphQLError.locations,
              extensions: graphQLError.extensions,
              nodes: graphQLErrorInstance?.nodes,
            },
          },
          fingerprint: [
            ...defaultCaptureContext.fingerprint,
            // use the graphql message in the fingerprint
            // since it may not be populated in the error message properly
            graphQLError.message,
          ],
        });
      }
    } else if (error.networkError) {
      if (
        error.graphQLErrors?.some(
          (error) => error.message === "Not signed in"
        ) ||
        (error.networkError?.name === "ServerError" &&
          (error.networkError as ServerError).statusCode === 401)
      ) {
        // if it's a 401 error, it should not be reported as an error
        // since it's expected to happen when the user's session expires
        return;
      }

      // if we _only_ have a network error, we can just capture that
      scrubAndCaptureException(error.networkError, {
        ...defaultCaptureContext,
        contexts: {
          ...defaultCaptureContext.contexts,
          apollo_network_error_details: {
            message: error.networkError.message,
            name: error.networkError.name,
            ...(error.networkError.name === "ServerError"
              ? {
                  status_code: (error.networkError as ServerError).statusCode,
                  status_text: (error.networkError as ServerError).response
                    .statusText,
                  response_json: (error.networkError as ServerError).result,
                }
              : {}),
          },
        },
      });
    } else {
      // capture raw errors
      scrubAndCaptureException(error, defaultCaptureContext);
    }
  } else {
    scrubAndCaptureException(error);
  }
}

const scrubAndCaptureException = (
  exception: any,
  captureContext?: Sentry.CaptureContext
) => {
  const scrubbedException = redactThing(exception, {
    redactEmails: true,
    redactPhoneNumbers: true,
  });
  const scrubbedCaptureContext = captureContext
    ? redactThing(captureContext, {
        redactEmails: true,
        redactPhoneNumbers: true,
      })
    : undefined;
  return Sentry.captureException(scrubbedException, scrubbedCaptureContext);
};

/**
 * Reports a breadcrumb to provide context leading up to a potential error
 */
export function reportErrorBreadcrumb(breadcrumb: {
  type?: "error";
  category?: string;
  level?: Sentry.SeverityLevel;
  data?: {
    [key: string]: any;
  };
}) {
  Sentry.addBreadcrumb({
    ...breadcrumb,
    level: "info",
  });
}

interface AppErrorBoundaryProps {
  children: ReactNode;
}

/**
 * This should wrap the application
 */
export function AppErrorBoundary({ children }: AppErrorBoundaryProps) {
  return (
    <Sentry.ErrorBoundary
      fallback={
        <ErrorPage
          message={
            <>
              An unexpected error occurred. Refresh the page to try again.
              <br />
              If the problem persists, please contact{" "}
              <a href="mailto:help@caribou.care">help@caribou.care</a>.
            </>
          }
        />
      }
    >
      {children}
    </Sentry.ErrorBoundary>
  );
}
